import pLimit from 'p-limit'

import type { ListTerritoriesWithMetricsQuery, ListTerritoriesWithMetricsQueryVariables } from 'generated/graphql'
import { LIST_TERRITORIES_WITH_METRICS } from 'graphql/territory/operations'
import type { TranslateFunction } from 'lib/tolgee'
import type { INormalizedDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'

import {
  SEARCH_RESULT_LEVEL_LABELS,
  SEARCH_RESULT_METRICS_CONCURRENCY,
  SEARCH_RESULT_METRICS_LOG,
} from './theme-list-tab.consts'
import type {
  ILoadSearchResultsMetricsParams,
  ISearchOption,
  ISearchTerritoryOptionResult,
  TSearchResultMetrics,
  TSearchResultMetricsClient,
  TSearchResultMetricsVariables,
  TSearchResultsMetricsAction,
  TSearchResultsMetricsState,
} from './theme-list-tab.types'
import type { TTableOrder } from '../common/global-topic.types'
import { handleSort } from '../common/global-topic.utils'

export const withSearchResultLevel = (
  results: INormalizedDataForTerritoriesTable[],
  t: TranslateFunction
): INormalizedDataForTerritoriesTable[] =>
  results.map(result => {
    const level = SEARCH_RESULT_LEVEL_LABELS[result.territoryId?.split('.')[0]]
    return { ...result, breadcrumbItems: level ? [t(level)] : [] }
  })

const normalizeForProximity = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const getNameProximity = (name: string, term: string) => {
  const normalizedName = normalizeForProximity(name)
  if (normalizedName === term) return 0
  if (normalizedName.startsWith(term)) return 1
  if (normalizedName.includes(term)) return 2
  return 3
}

export const sortByNameProximity = <TResult extends ISearchTerritoryOptionResult>(
  results: readonly TResult[],
  term: string
): TResult[] => {
  const normalizedTerm = normalizeForProximity(term)
  return results
    .map((result, index) => ({ result, index, proximity: getNameProximity(result.name, normalizedTerm) }))
    .sort((a, b) => a.proximity - b.proximity || a.index - b.index)
    .map(({ result }) => result)
}

export const getSearchOptions = (
  results: readonly ISearchTerritoryOptionResult[] | undefined,
  term: string,
  seeResultsLabel: string,
  t: TranslateFunction
): ISearchOption[] => {
  if (!results?.length) return []

  const territoryOptions = sortByNameProximity(results, term).map(({ territoryId, name }) => {
    const level = SEARCH_RESULT_LEVEL_LABELS[territoryId.split('.')[0]]
    return { label: name, subLabel: level ? t(level) : null, value: territoryId, searchTerm: term }
  })

  return [{ label: seeResultsLabel, subLabel: null, value: null, searchTerm: term }, ...territoryOptions]
}

const fetchSearchResultMetrics = async (
  client: TSearchResultMetricsClient,
  territoryId: string,
  variables: TSearchResultMetricsVariables
): Promise<TSearchResultMetrics> => {
  const { data } = await client.query<ListTerritoriesWithMetricsQuery, ListTerritoriesWithMetricsQueryVariables>({
    query: LIST_TERRITORIES_WITH_METRICS,
    variables: { ...variables, ids: [territoryId] },
    context: { errorLog: SEARCH_RESULT_METRICS_LOG },
  })
  const performance = data?.listTerritories?.[0]?.territoryPerformance
  return {
    totalPosts: performance?.totalPosts ?? 0,
    avgEngagement: performance?.sampledMetrics?.avgEngagement ?? 0,
    avgViews: performance?.sampledMetrics?.avgViews ?? 0,
  }
}

const withOneRetry = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request()
  } catch {
    return request()
  }
}

export const loadSearchResultsMetrics = async ({
  client,
  territoryIds,
  variables,
  onResultSettled,
  isCancelled,
}: ILoadSearchResultsMetricsParams): Promise<void> => {
  const limitMetrics = pLimit(SEARCH_RESULT_METRICS_CONCURRENCY)

  await Promise.all(
    territoryIds.map(territoryId =>
      limitMetrics(async () => {
        if (isCancelled()) return
        const metrics = await withOneRetry(() => fetchSearchResultMetrics(client, territoryId, variables)).catch(
          () => null
        )
        if (!isCancelled()) onResultSettled(territoryId, metrics)
      })
    )
  )
}

export const searchResultsMetricsReducer = (
  state: TSearchResultsMetricsState | undefined,
  { key, territoryId, metrics }: TSearchResultsMetricsAction
): TSearchResultsMetricsState => {
  const current = state?.key === key ? state : { key, metricsById: {}, failedIds: [] }

  return metrics
    ? { ...current, metricsById: { ...current.metricsById, [territoryId]: metrics } }
    : { ...current, failedIds: [...current.failedIds, territoryId] }
}

export const withSearchResultMetrics = (
  results: INormalizedDataForTerritoriesTable[],
  { metricsById, failedIds }: Pick<TSearchResultsMetricsState, 'metricsById' | 'failedIds'>
): INormalizedDataForTerritoriesTable[] =>
  results.map(result => {
    const metrics = metricsById[result.territoryId]
    if (metrics) return { ...result, ...metrics, totalVideos: metrics.totalPosts }
    const isMetricsUnavailable = failedIds.includes(result.territoryId)
    return { ...result, isMetricsLoading: !isMetricsUnavailable, isMetricsUnavailable }
  })

export const sortSearchResults = (
  results: INormalizedDataForTerritoriesTable[],
  order: TTableOrder,
  failedIds: string[]
): INormalizedDataForTerritoriesTable[] => {
  const measured = results.filter(result => !failedIds.includes(result.territoryId))
  const failed = results.filter(result => failedIds.includes(result.territoryId))
  return [...handleSort(measured, order), ...failed]
}
